// DetailHero — section 1. Canonical detail.html lines 162–196.
//
// Presentation-only. Status/deal/type label dictionaries locally
// duplicated per CLAUDE.md SR-6 — the realtyList feature owns its
// own copy in PropertyCard.js; the detail feature owns its own here.

import {
  getPropertyStatusLabel,
  getPropertyStatusTone,
} from "../../realty/lib/propertyLifecycle";
import { buildFullLocationLine } from "../../geo/lib/locationPresentation";

const DEAL_TYPE_LABELS = {
  sale: "Продаж",
  rent: "Оренда",
};

const PROPERTY_TYPE_LABELS = {
  apartment: "Квартира",
  house: "Будинок",
  land: "Земельна ділянка",
  commercial: "Комерційна",
  industrial: "Промислова",
};

const PROPERTY_TYPE_ICONS = {
  apartment: "fa-solid fa-building",
  house: "fa-solid fa-house-chimney",
  land: "fa-solid fa-mountain-sun",
  commercial: "fa-solid fa-store",
  industrial: "fa-solid fa-industry",
};

const CURRENCY_GLYPH = {
  USD: "$",
  EUR: "€",
  UAH: "₴",
};

const formatPrice = (amount, currency) => {
  if (amount === null || amount === undefined || amount === "") return null;
  const numeric = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(numeric)) return null;
  const formatted = numeric.toLocaleString("uk-UA", {
    maximumFractionDigits: 2,
  });
  const glyph = CURRENCY_GLYPH[currency] || "";
  return `${glyph}${formatted}`.trim();
};

const buildHeroAddress = (location) => buildFullLocationLine(location);

const computePricePerM2 = (property) => {
  const price = Number(property?.price);
  if (!Number.isFinite(price) || price <= 0) return null;
  const residentialArea = Number(property?.residential?.total_area_m2);
  if (Number.isFinite(residentialArea) && residentialArea > 0) {
    return Math.round(price / residentialArea);
  }
  const commercialArea = Number(property?.commercial?.total_area_m2);
  if (Number.isFinite(commercialArea) && commercialArea > 0) {
    return Math.round(price / commercialArea);
  }
  return null;
};

function DetailHero({ property }) {
  const status = property.status || "active";
  const dealType = property.deal_type || "sale";
  const propertyType = property.property_type || "apartment";

  const statusLabel = getPropertyStatusLabel(status);
  const statusTone = getPropertyStatusTone(status);
  const dealLabel = DEAL_TYPE_LABELS[dealType] || dealType;
  const typeLabel = PROPERTY_TYPE_LABELS[propertyType] || propertyType;
  const typeIcon = PROPERTY_TYPE_ICONS[propertyType] || "fa-solid fa-house";

  const priceText = formatPrice(property.price, property.currency);
  const pricePerM2 = computePricePerM2(property);
  const addressLine = buildHeroAddress(property.location);

  return (
    <section className="detail-hero">
      <div className="container">
        <div className="detail-hero__inner">
          <div
            className="detail-hero__chips"
            role="group"
            aria-label="Класифікація оголошення"
          >
            <span
              className={`status-chip status-chip--${statusTone}`}
              title="Статус оголошення"
            >
              <span className="dot" aria-hidden="true" />
              {statusLabel}
            </span>
            <span
              className={`deal-chip deal-chip--${dealType}`}
              title="Тип угоди"
            >
              <i className="fa-solid fa-tag" aria-hidden="true" />
              {dealLabel}
            </span>
            <span className="type-chip" title="Тип нерухомості">
              <i className={typeIcon} aria-hidden="true" />
              {typeLabel}
            </span>
          </div>

          <h1 className="detail-hero__title">
            {property.title || <span className="muted">—</span>}
          </h1>

          <a className="detail-hero__address" href="#detail-location">
            <i className="fa-solid fa-location-dot" aria-hidden="true" />
            <span>
              {addressLine || <span className="muted">—</span>}
            </span>
          </a>

          <div className="detail-hero__price">
            <div className="price-main">
              {priceText ? (
                <span className="price-amount">{priceText}</span>
              ) : (
                <span className="price-amount muted">—</span>
              )}
              {property.currency ? (
                <span className="price-currency">{property.currency}</span>
              ) : null}
            </div>
            <div className="price-meta">
              {pricePerM2 !== null ? (
                <span className="price-per-m">
                  {CURRENCY_GLYPH[property.currency] || ""}
                  {pricePerM2} / м<sup>2</sup>
                </span>
              ) : (
                <span className="price-per-m muted">—</span>
              )}
              {/* TODO: backend-truth-missing — no MLS field on Property
                  model. Pre-deal MLS hint lives in description_private
                  text only. Slot preserved per canonical hero layout. */}
              <span className="price-mls muted">—</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default DetailHero;
